import { configureStore } from "@reduxjs/toolkit";
import { combineReducers } from "redux";
import bookingSlice from "./slice/booking/bookingSlice";
import apartmentRentalSlice from "./slice/apartmentRental/apartmentRentalSlice";
import configSlice from "./slice/config/configSlice";
import { locationsApiSlice } from "../api/locations/locationsApiSlice";
import bookingCreationSlice from "./slice/bookingCreation/bookingCreationSlice";
import itemRetrievalSlice from "./slice/itemRetrieval/itemRetrievalSlice";
import temporaryStorageSlice from "./slice/temporaryStorage/temporaryStorageSlice";
import senderRetrievalSlice from "./slice/senderRetrieval/senderRetrievalSlice";
import notificationSlice from "./slice/notification/notificationSlice";
import subscriptionSlice from "./slice/subscription/subscriptionSlice";
import { availabilitiesApiSlice, supAvailabilitiesApiSlice } from "../api/availabilities/availabilitiesApiSlice";
import { reservationsApiSlice } from "../api/reservations/reservationsApiSlice";
import { paymentsApiSlice } from "../api/payments/paymentsApiSlice";
import { bookingsApiSlice } from "../api/bookings/bookingsApiSlice";
import hintsSlice from "./slice/hints/hintsSlice";
import { hintsApiSlice } from "../api/hints/hintsApiSlice";
import { rentalsApiSlice } from "../api/rentals/rentalsApiSlice";
import { lockersApiSlice } from "../api/lockers/lockersApiSlice";
import { issueReportApiSlice } from "../api/issueReport/issueReportApiSlice";
import { pricePreviewApiSlice } from "../api/pricePreview/pricePreviewApiSlice";
import { roomWeekApiSlice } from "../api/roomWeek/roomWeekApiSlice";
import { categoryRoomsApiSlice } from "../api/categoryRooms/categoryRoomsApiSlice";
import consentSlice from "./slice/consent/consentSlice";
import { receiverApiSlice } from "../api/receiver/receiverApiSlice";
import { apartmentBookingApiSlice } from "../api/apartment/apartmentApiSlice";
import { vouchersApiSlice } from "../api/voucher/voucherApiSlice";
import { infoApiSlice } from "../api/info/infoApiSlice";
import { commercialModelsApiSlice } from "../api/commercialModels/commercialModelsApiSlice";
import { customerAgreementsApiSlice } from "api/customerAgreements/customerAgreementsApiSlice";
import { usageLogApiSlice } from "../api/usageLog/usageLogApiSlice";
import { accountApiSlice } from "../api/account/accountApiSlice";
import { ideaSubmissionApiSlice } from "../api/ideaSubmission/ideaSubmissionApiSlice";
import accountSlice from "./slice/account/accountSlice";
import { API_SLICES } from "./apiSlices";
import { accountCacheListener } from "./accountScopedCaches";

const reducers = combineReducers({
  [locationsApiSlice.reducerPath]: locationsApiSlice.reducer,
  [availabilitiesApiSlice.reducerPath]: availabilitiesApiSlice.reducer,
  [supAvailabilitiesApiSlice.reducerPath]: supAvailabilitiesApiSlice.reducer,
  [reservationsApiSlice.reducerPath]: reservationsApiSlice.reducer,
  [bookingsApiSlice.reducerPath]: bookingsApiSlice.reducer,
  [paymentsApiSlice.reducerPath]: paymentsApiSlice.reducer,
  [hintsApiSlice.reducerPath]: hintsApiSlice.reducer,
  [rentalsApiSlice.reducerPath]: rentalsApiSlice.reducer,
  [lockersApiSlice.reducerPath]: lockersApiSlice.reducer,
  [issueReportApiSlice.reducerPath]: issueReportApiSlice.reducer,
  [receiverApiSlice.reducerPath]: receiverApiSlice.reducer,
  [apartmentBookingApiSlice.reducerPath]: apartmentBookingApiSlice.reducer,
  [vouchersApiSlice.reducerPath]: vouchersApiSlice.reducer,
  [infoApiSlice.reducerPath]: infoApiSlice.reducer,
  [commercialModelsApiSlice.reducerPath]: commercialModelsApiSlice.reducer,
  [customerAgreementsApiSlice.reducerPath]: customerAgreementsApiSlice.reducer,
  [usageLogApiSlice.reducerPath]: usageLogApiSlice.reducer,
  [accountApiSlice.reducerPath]: accountApiSlice.reducer,
  [ideaSubmissionApiSlice.reducerPath]: ideaSubmissionApiSlice.reducer,
  [pricePreviewApiSlice.reducerPath]: pricePreviewApiSlice.reducer,
  [roomWeekApiSlice.reducerPath]: roomWeekApiSlice.reducer,
  [categoryRoomsApiSlice.reducerPath]: categoryRoomsApiSlice.reducer,
  booking: bookingSlice,
  tenantConfig: configSlice,
  bookingCreation: bookingCreationSlice,
  subscription: subscriptionSlice,
  itemRetrieval: itemRetrievalSlice,
  temporaryStorage: temporaryStorageSlice,
  senderRetrieval: senderRetrievalSlice,
  notification: notificationSlice,
  hints: hintsSlice,
  consent: consentSlice,
  apartment: apartmentRentalSlice,
  account: accountSlice
});

export const baseStore = configureStore({
  reducer: reducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // serializableCheck: {
      //   ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      // },
      serializableCheck: false,      
      immutableCheck: false,
    })
      .concat(API_SLICES.map((slice) => slice.middleware))
      // Evicts the caller-dependent caches when the account identity changes
      // (see redux/accountScopedCaches.ts). Prepended so it observes the
      // pre-action state.
      .prepend(accountCacheListener.middleware),
});

export type RootState = ReturnType<typeof baseStore.getState>;
export type AppDispatch = typeof baseStore.dispatch;
